<template>
    <div class="col-lg-12 mx-2">
        <ContentContainer>
            <template v-slot:content>
                <div class="row d-flex m-auto">
                    <div v-for="item in visibleAdminToolsCards" :key="item.id || item.label"
                        class="col-lg-4 col-md-5 col-12">
                        <TabCard :text="item.label" :icon="getCardIcon(item.label)" @click="redirectToPage(item)" />
                    </div>

                </div>

                <Modal v-if="isModalOpen" :modalHeading="modalHeading" :modalContent="modalContent"
                    :iframeUrl="iframeUrl" refresh-on-close @close="closeModal">
                </Modal>

                <ResponseStatsModal v-model="responseStatsOpen" />
                <UserLookupModal v-model="userLookupOpen" />
                <AutoEngineInsightsModal v-model="autoEngineInsightsOpen" />

                <!-- Audit Events Card -->
                <AuditEventsCard scope="all" :showOrganization="true" />

                <!-- Volunteer invitations overview -->
                <VolunteerInvitationAdminCard v-if="isSuperAdmin" scope="all" />

                <!-- Superadmin Messages Card -->
                <AdminMessagesCard v-if="isSuperAdmin" />

                <!-- Lead Generation & Outreach Card (superadmin only) -->
                <LeadsAdminCard v-if="isSuperAdmin" />
            </template>
        </ContentContainer>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Modal from '@/components/Modal/Modal.vue';
import ContentContainer from '@/components/PageLayout/ContentContainer.vue';
import TabCard from '@/components/Cards/TabCards.vue';
import AuditEventsCard from '@/components/Cards/AuditEventsCard.vue';
import VolunteerInvitationAdminCard from '@/components/Admin/VolunteerInvitationAdminCard.vue';
import AdminMessagesCard from '@/components/Admin/AdminMessagesCard.vue';
import LeadsAdminCard from '@/components/Admin/Leads/LeadsAdminCard.vue';
import ResponseStatsModal from '@/components/Admin/ResponseStatsModal.vue';
import UserLookupModal from '@/components/Admin/UserLookupModal.vue';
import AutoEngineInsightsModal from '@/components/Admin/AutoEngineInsightsModal.vue';
import { getLocationId } from '@/utils/SessionDataHandler';
import { processUriPlaceholders } from '@/utils/UriHandler';
import { apiBaseUrl } from '@/utils/BaseUrl';
import { usePermissions } from '@/composables/usePermissions';

const store = useStore();
const router = useRouter();
const { isSuperAdmin } = usePermissions();

// Vuex getters
const stSessionData = computed(() => store.getters.stSessionData);
const stAdminToolsCards = computed(() => store.getters.stAdminToolsCards);
const stPermissionData = computed(() => store.getters.stPermissionData);

// Local state
const isModalOpen = ref(false);
const modalHeading = ref("");
const modalContent = ref("");
const iframeUrl = ref("");
const responseStatsOpen = ref(false);
const userLookupOpen = ref(false);
const autoEngineInsightsOpen = ref(false);

// Process admin tools cards with dynamic URIs
const adminToolsCards = computed(() => {
    if (!stAdminToolsCards.value || stAdminToolsCards.value.length === 0) {
        return [];
    }

    return processUriPlaceholders(stAdminToolsCards.value, {
        locationId: stSessionData.value?.locationid || getLocationId()
    });
});

const visibleAdminToolsCards = computed(() => {
    const allowed = stPermissionData.value?.adminToolsCards;
    if (!allowed?.length) {
        return adminToolsCards.value;
    }
    return adminToolsCards.value.filter((card) =>
        allowed.includes(card?.id || card?.label)
    );
});

// Map each card to different icon and color
function getCardIcon(label) {
    const iconMap = {
        "View Logs": {
            name: "description",
            color: "text-white",
            background: "info"
        },
        "View Organizations": {
            name: "business",
            color: "text-white",
            background: "success"
        },
        "Stats": {
            name: "pie_chart",
            color: "text-white",
            background: "info"
        },
        "User Lookup": {
            name: "person_search",
            color: "text-white",
            background: "primary"
        },
        "Auto-Engine Insights": {
            name: "query_stats",
            color: "text-white",
            background: "success"
        },
        "Api List": {
            name: "api",
            color: "text-white",
            background: "warning"
        }
    };

    return iconMap[label] || {
        name: "weekend",
        color: "text-white",
        background: "dark"
    };
}

// Methods
function redirectToPage(item) {
    if (item.id === 'superadmin_stats') {
        responseStatsOpen.value = true;
    } else if (item.id === 'superadmin_user_lookup') {
        userLookupOpen.value = true;
    } else if (item.id === 'superadmin_auto_engine_insights') {
        autoEngineInsightsOpen.value = true;
    } else if (item.id === 'superadmin_apilist') {
        openSwaggerModal();
    } else if (item.uri_type === 'modal') {
        openModal(item);
    } else if (item.uri_type === 'page') {
        router.push(item.uri);
    } else {
        router.push(item.uri || item.url);
    }
}

function openSwaggerModal() {
    // Set JWT as a cookie so the backend jwt.cookie middleware can authenticate the iframe request
    const token = localStorage.getItem('token');
    if (token) {
        document.cookie = `token=${token}; path=/; SameSite=Lax`;
    }

    isModalOpen.value = true;
    modalHeading.value = 'Api List';
    iframeUrl.value = `${apiBaseUrl}/documentation`;
}

function openModal(item) {
    isModalOpen.value = true;
    modalHeading.value = item.label;
    iframeUrl.value = item.uri || item.url;
}

function closeModal() {
    isModalOpen.value = false;
}
</script>